import React, { useState, useEffect } from 'react';
import { Translate } from 'react-localize-redux';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  FormFieldSize,
  MultiSelect,
  Button,
  ButtonType,
  Icon,
  Checkbox,
  Input
} from '@digicert/dcone-common-ui';
import styles from './GroupAccess.module.scss';
import { faExternalLinkSquare, faMinusCircle, faSearch } from '@fortawesome/pro-light-svg-icons';
import { serviceIcon, translate } from 'shared/helpers/utils';
import { GroupAccessDrawerProps } from 'secure-pages/groups/Groups.types';

const GroupAccessDrawer = ({
  open,
  onClose,
  groupName,
  availableServices,
  selectedServices,
  setSelectedServices,
  serviceRoleData,
  selectedRoles,
  setSelectedRoles,
  assignedRolesList,
  removeAssignedRole,
  handleAssignAccess,
  handleCancel,
  hasExistingRoles
}: GroupAccessDrawerProps) => {
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open]);

  const getServiceRolesDocLink = (serviceKey: string): string => {
    const servicePrefix = serviceKey.split('.')[0];
    const serviceRolesLinks: Record<string, string> = {
      'cc': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/certcentral-user-role.html',
      'cm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/ca-manager-user-roles.html',
      'devctm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/device-trust-user-roles.html',
      'dtm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/document-trust-manager-user-roles.html',
      'ctm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/document-trust-manager-user-roles.html',
      'iot': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/iot-trust-manager-user-roles.html',
      'stm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/software-trust-manager-user-roles.html',
      'tlm': 'https://docs.digicert.com/en/platform-overview/manage-your-accounts/digicert-account/users/user-roles/list-of-user-roles/trust-lifecycle-manager-user-roles.html'
    };
    return serviceRolesLinks[servicePrefix];
  };

  const handleRoleToggle = (serviceKey: string, roleId: string) => {
    const currentSelected = selectedRoles[serviceKey] || [];
    const newSelected = currentSelected.includes(roleId)
      ? currentSelected.filter(id => id !== roleId)
      : [...currentSelected, roleId];

    setSelectedRoles({
      ...selectedRoles,
      [serviceKey]: newSelected
    });
  };

  return (
    <Drawer open={open} onClose={onClose} width={1100}>
      <DrawerHeader
        label={groupName}
        description={translate('accounts.details.scim.groups.assignAccess') as string}
        onClose={onClose}
        closable={true}
      />
      <DrawerContent className={styles.drawerContent}>
        <div className={styles.drawerScrollArea}>
          <div className={styles.drawerGrid}>
            <div className={styles.leftPane}>
              <div className={styles.formGroup}>
                <MultiSelect
                  label={translate('accounts.details.scim.groups.servicesLabel') as string}
                  options={availableServices}
                  value={selectedServices}
                  onChange={setSelectedServices}
                  placeholder={translate('accounts.details.scim.groups.selectServices') as string}
                  className={styles.select}
                  size={FormFieldSize.XXL}
                  allowClear
                  mode="multiple"
                />
              </div>
              <div className={styles.serviceRolesSection}>
                {selectedServices.length === 0 && (
                  <div className={styles.emptyStateContainer}>
                    <div className={styles.emptyState}>
                      <p className={styles.emptyStateTitle}>
                        <Translate id='accounts.details.scim.groups.selectServicesToViewRoles' />
                      </p>
                      <p className={styles.emptyStateText}>
                        <Translate id='accounts.details.scim.groups.selectOneOrMoreServices' />
                      </p>
                    </div>
                  </div>
                )}
                {selectedServices.length > 0 && !selectedServices.some(serviceKey => {
                  const serviceData = serviceRoleData[serviceKey];
                  return serviceData && serviceData.roles.length > 0;
                }) && (
                  <div className={styles.emptyStateContainer}>
                    <div className={styles.emptyState}>
                      <p className={styles.emptyStateTitle}>
                        <Translate id='accounts.details.scim.groups.noRolesAvailable' />
                      </p>
                    </div>
                  </div>
                )}
                {selectedServices.length > 0 && selectedServices.some(serviceKey => {
                  const serviceData = serviceRoleData[serviceKey];
                  return serviceData && serviceData.roles.length > 0;
                }) && (
                  <div className={styles.searchContainer}>
                    <Input
                      placeholder={translate('accounts.details.scim.groups.searchRolesAcrossProducts') as string}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                )}
                {searchText && !selectedServices.some(serviceKey => {
                  const serviceData = serviceRoleData[serviceKey];
                  return serviceData && serviceData.roles.some(role =>
                    role.roleName?.toLowerCase().includes(searchText.toLowerCase())
                  );
                }) && (
                  <div className={styles.emptyStateContainer}>
                    <div className={styles.emptyState}>
                      <p className={styles.emptyStateTitle}>
                        <Translate id='accounts.details.scim.groups.noRolesMatchSearch' />
                      </p>
                    </div>
                  </div>
                )}
                {selectedServices.map(serviceKey => {
                  const service = availableServices.find(s => s.value === serviceKey);
                  const serviceData = serviceRoleData[serviceKey];
                  if (!service || !serviceData || serviceData.roles.length === 0) return null;

                  const filteredRoles = serviceData.roles.filter(role =>
                    role.roleName?.toLowerCase().includes(searchText.toLowerCase())
                  );

                  if (filteredRoles.length === 0) return null;

                  const currentSelected = selectedRoles[serviceKey] || [];

                  return (
                    <div key={serviceKey} className={styles.serviceRolesBlock}>
                      <div className={styles.serviceRolesHeader}>
                        <div className={styles.serviceIcon}>{serviceIcon(serviceKey)}</div>
                        <span className={styles.serviceName}>{service.label}</span>
                        <a
                          href={getServiceRolesDocLink(serviceKey)}
                          className={styles.compareRolesLink}
                          tabIndex={-1}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {translate('accounts.details.scim.groups.compareRoles') as string} {service.label} {translate('accounts.details.scim.groups.roles') as string} <Icon className={styles.externalLinkIcon} icon={faExternalLinkSquare}></Icon>
                        </a>
                      </div>
                      <div className={styles.accountInfo}>
                        <div className={styles.accountLabel}>{service.label} {translate('accounts.details.scim.groups.serviceAccount') as string}</div>
                        <div className={styles.accountDetails}>{serviceData.account} ({serviceData.sp_account_friendly_name || serviceData.sp_account_id})</div>
                      </div>
                      <div className={styles.serviceRolesContent}>
                        <div className={styles.customTable}>
                          <div className={styles.tableHeader}>
                            <div className={styles.roleNameHeader}>
                              {translate('accounts.details.scim.groups.roleName') as string}
                            </div>
                          </div>
                          <div className={styles.tableBody}>
                            {filteredRoles.map(role => (
                              <div
                                key={role.id}
                                className={`${styles.tableRow} ${currentSelected.includes(role.id) ? styles.selectedRow : ''}`}
                                onClick={() => handleRoleToggle(serviceKey, role.id)}
                              >
                                <div className={styles.checkboxCell}>
                                  <Checkbox
                                    checked={currentSelected.includes(role.id)}
                                    onChange={() => handleRoleToggle(serviceKey, role.id)}
                                  />
                                </div>
                                <div className={styles.roleNameCell}>{role.roleName}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.rightPane}>
              <div className={styles.assignedRolesHeader}>
                {translate('accounts.details.scim.groups.assignedRoles') as string}
              </div>
              <div className={styles.assignedRolesList}>
                {assignedRolesList.length === 0 ? (
                  <div className={styles.assignedRolesEmpty}>
                    <Translate id='accounts.details.scim.groups.noRolesAssignedDrawer' />
                  </div>
                ) : (
                  assignedRolesList.map((item) => (
                    <div key={item.serviceKey + item.roleId} className={styles.assignedRoleItem}>
                      <div className={styles.assignedRoleInfo}>
                        <span className={styles.assignedRoleService}>{item.serviceLabel} - {item.account}</span>
                        <span className={styles.assignedRoleName}>{item.roleName}</span>
                      </div>
                      <Icon icon={faMinusCircle} onClick={() => removeAssignedRole(item.serviceKey, item.roleId)} aria-label="Remove" className={styles.removeRoleBtn}></Icon>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
      <DrawerFooter>
        <div className={styles.drawerFooter}>
          <Button
            buttonType={ButtonType.PRIMARY}
            onClick={handleAssignAccess}
            disabled={!hasExistingRoles && assignedRolesList.length === 0}
          >
            <Translate id={hasExistingRoles ? 'accounts.details.scim.groups.updateAccess' : 'accounts.details.scim.groups.assignAccess'} />
          </Button>
          <Button
            onClick={handleCancel}
            buttonType={ButtonType.SECONDARY}
          >
            <Translate id='accounts.details.scim.groups.cancel' />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default GroupAccessDrawer;
